<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const emit = defineEmits(['created']);

const store = useStore();
const { t } = useI18n();

const dialogRef = ref(null);
const searchQuery = ref('');
const selectedAgentId = ref(null);
const isCreating = ref(false);

const currentUserId = computed(() => store.getters.getCurrentUser?.id);

const agents = computed(() => store.getters['agents/getAgents'] || []);

const filteredAgents = computed(() => {
  const list = agents.value.filter(agent => agent.id !== currentUserId.value);
  if (!searchQuery.value) return list;
  const query = searchQuery.value.toLowerCase();
  return list.filter(agent => (agent.name || '').toLowerCase().includes(query));
});

const isFormValid = computed(() => selectedAgentId.value !== null);

function open() {
  searchQuery.value = '';
  selectedAgentId.value = null;
  store.dispatch('agents/get');
  dialogRef.value?.open();
}

function selectAgent(agentId) {
  selectedAgentId.value = agentId;
}

async function handleConfirm() {
  if (!isFormValid.value) return;

  isCreating.value = true;
  try {
    const result = await store.dispatch('internalChat/create', {
      channel: { channel_type: 'dm' },
      member_ids: [selectedAgentId.value],
    });
    dialogRef.value?.close();
    emit('created', result.id);
  } catch {
    // error handled by throwErrorMessage in the action
  } finally {
    isCreating.value = false;
  }
}

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('INTERNAL_CHAT.DM.NEW')"
    :confirm-button-label="t('INTERNAL_CHAT.DM.NEW')"
    :disable-confirm-button="!isFormValid"
    :is-loading="isCreating"
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-3">
      <input
        v-model="searchQuery"
        type="text"
        class="w-full rounded-lg border border-n-slate-6 bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 placeholder-n-slate-10 outline-none focus:border-n-brand"
        :placeholder="t('INTERNAL_CHAT.DM.SELECT_AGENTS')"
      />
      <div class="flex flex-col gap-1 max-h-64 overflow-y-auto">
        <button
          v-for="agent in filteredAgents"
          :key="agent.id"
          type="button"
          class="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-left transition-colors"
          :class="
            selectedAgentId === agent.id
              ? 'bg-n-brand/10 text-n-slate-12'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="selectAgent(agent.id)"
        >
          <Avatar
            :name="agent.name || ''"
            :src="agent.thumbnail || ''"
            :size="24"
            rounded-full
          />
          <span class="truncate">{{ agent.name }}</span>
        </button>
      </div>
    </div>
  </Dialog>
</template>
